import { io, Socket } from "socket.io-client";

const WS_URL = process.env.REACT_APP_API_SOCKET_URL;

class SocketHandler {
  private socket: Socket | null = null;
  private static instance: SocketHandler;
  private messageHandlers: Map<string, (data: any) => void> = new Map();
  private subscribedEvents: Set<string> = new Set();

  private constructor() {
    this.initializeSocket();
  }

  public static getInstance(): SocketHandler {
    if (!SocketHandler.instance) {
      SocketHandler.instance = new SocketHandler();
    }
    return SocketHandler.instance;
  }

  private initializeSocket() {
    this.socket = io(WS_URL, {
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on("connect", () => {
      console.log("Socket.IO connected successfully");
    });

    this.socket.on("disconnect", () => {
      console.log("Socket.IO disconnected");
    });

    this.socket.on("error", (error: Error) => {
      console.error("Socket.IO error:", error);
    });
  }

  public subscribeToWhiteBoard(cardId: string, callback: (data: any) => void) {
    const eventName = `on-card-message-received-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromWhiteboard(bookingId: string) {
    const eventName = `on-card-message-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToBooking(bookingId: string, callback: (data: any) => void) {
    const eventName = `on-message-received-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      console.log("Subscribing to booking:", bookingId);
      
      this.socket.on(eventName, (data: any) => {
        console.log("Looking socket for poll", eventName, data);
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromBooking(bookingId: string) {
    const eventName = `on-message-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToPVLC(cardId: string, callback: (data: any) => void) {
    const eventName = `on-card-message-received-${cardId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (cardId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromPVLC(cardId: string) {
    const eventName = `on-card-pvlc-status-received-${cardId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToNotification(
    userId: string,
    callback: (data: any) => void
  ) {
    const eventName = `on-notification-received-${userId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        console.log("notification data", data);
        if (userId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeFromNotification(userId: string) {
    const eventName = `on-notification-received-${userId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToBingoEvent(
    bookingId: string,
    callback: (data: any) => void,
    opts?: { debug?: boolean }            // ← optional
  ) {
    const eventName = `on-bingo-event-${bookingId}`;

    // Debug toggle: ?wsdebug=1 in URL, or pass opts.debug=true, or dev mode
    const debug =
      opts?.debug ??
      (typeof window !== "undefined" &&
        (new URLSearchParams(window.location.search).get("wsdebug") === "1" ||
          process.env.NODE_ENV !== "production"));

    // Update/replace handler for this event
    this.messageHandlers.set(eventName, callback);

    if (this.socket && !this.subscribedEvents.has(eventName)) {
      if (debug) {
        console.log(`[BingoWS] subscribe → ${eventName}`, { bookingId });
      }

      this.socket.on(eventName, (data: any) => {
        // Normalize type from server
        const type = data?.type || data?.event || "unknown";

        if (debug) {
          console.groupCollapsed(
            `[BingoWS] recv ← ${eventName} | type="${type}"`
          );
          console.log("bookingId:", bookingId);
          console.log("payload:", data);
        }

        // Special: Bingo STARTED by talent (highlight + details)
        if (type === "bingo_start" || type === "bingo.start") {
          const details = {
            session_id: data?.id ?? data?.session_id,
            booking_id: data?.booking_id,
            host_id: data?.host_id,
            status: data?.status,
            created_at: data?.created_at,
            seed: data?.seed ?? data?.sessionId,
          };
          if (debug) {
            console.info("🎉 [BingoWS] BINGO STARTED by talent", details);
          }
        }

        if (debug) console.groupEnd();

        // Dispatch to the latest registered handler
        const handler = this.messageHandlers.get(eventName);
        if (handler) handler(data);
      });

      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToBingoEvent(bookingId: string) {
    const eventName = `on-bingo-event-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToStageEvent(bookingId: string, callback: (data: any) => void) {
    const eventName = `on-stagecard-action-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToStageEvent(bookingId: string) {
    const eventName = `on-stagecard-action-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToGuestInvitationReceived(bookingId: string, userId: string | number, callback: (data: any) => void) {
    const eventName = `on-stagecard-invite-received-${bookingId}-${userId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToGuestInvitationReceived(bookingId: string, userId: string | number) {
    const eventName = `on-stagecard-invite-received-${bookingId}-${userId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  public subscribeToYoutubeUrlReceived(bookingId: string, callback: (data: any) => void) {
    const eventName = `on-youtube-url-received-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  public unsubscribeToYoutubeUrlReceived(bookingId: string) {
    const eventName = `on-youtube-url-received-${bookingId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

  /* ------------------------------------------------------------------------
     NEW: Moderator umbrella subscription helpers
     ------------------------------------------------------------------------ */

  /** Central list of booking-scoped events a moderator should receive. */
  private getModeratorEventNames(bookingId: string): string[] {
    return [
     
      `on-youtube-url-received-${bookingId}`,
      `on-stagecard-action-${bookingId}`,
      `on-bingo-event-${bookingId}`,
      `on-message-received-${bookingId}`,
    ];
  }

  /**
   * Subscribe (or re-subscribe) to all moderator-specific events for a booking.
   * Keeps single socket listener per event, but updates the handler reference.
   */
  public subscribeToModerator(bookingId: string, userId: string | number, callback: (data: any) => void) {
    const eventName = `on-stagecard-invite-received-${bookingId}-${userId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  /** Unsubscribe from all moderator-specific events for a booking. */
   public unsubscribeToModerator(bookingId: string, userId: string | number) {
    const eventName = `on-stagecard-invite-received-${bookingId}-${userId}`;
    this.messageHandlers.delete(eventName);

    // Remove the event listener if no handlers are left for this event
    if (this.socket && this.subscribedEvents.has(eventName)) {
      this.socket.off(eventName);
      this.subscribedEvents.delete(eventName);
    }
  }

   public subscribeToModeratorAccept(bookingId: string, callback: (data: any) => void) {
    const eventName = `on-stagecard-action-${bookingId}`;

    // Update the handler for this event
    this.messageHandlers.set(eventName, callback);

    // Only add the event listener if it hasn't been added before
    if (this.socket && !this.subscribedEvents.has(eventName)) {
      this.socket.on(eventName, (data: any) => {
        if (bookingId) {
          const handler = this.messageHandlers.get(eventName);
          if (handler) {
            handler(data);
          }
        }
      });
      this.subscribedEvents.add(eventName);
    }
  }

  /** Optional convenience: sync subscriptions based on a boolean moderator flag. */


  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.messageHandlers.clear();
    this.subscribedEvents.clear();
  }
}

export default SocketHandler
